import React from 'react';
import { Settings, User, Building2, Bell, Shield, CreditCard, Globe, Database } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

const Configuracoes: React.FC = () => {
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useTheme();

  const settingsCategories = [
    {
      title: 'Perfil da Empresa',
      description: 'Configure as informações básicas da sua escola',
      icon: Building2,
      items: [
        { name: 'Dados da Empresa', description: 'Nome, CNPJ, endereço', href: '/configuracoes/dados-empresa' },
        { name: 'Logo e Identidade', description: 'Upload de logo e cores', href: '/configuracoes/logo-identidade' },
        { name: 'Contatos', description: 'Telefone, email, redes sociais', href: '/configuracoes/contatos' }
      ]
    },
    {
      title: 'Usuários e Permissões',
      description: 'Gerencie usuários e níveis de acesso',
      icon: User,
      items: [
        { name: 'Usuários Administrativos', description: 'Adicionar e gerenciar usuários', href: '/configuracoes/usuarios' },
        { name: 'Perfis de Acesso', description: 'Definir níveis de acesso', href: '/configuracoes/perfis-acesso' },
        { name: 'Professores', description: 'Configurar acesso dos professores' }
      ]
    },
    {
      title: 'Integrações',
      description: 'Configure integrações com serviços externos',
      icon: CreditCard,
      items: [
        { name: 'Gateway de Pagamento', description: 'Configurar Asaas e outros gateways' },
        { name: 'PIX', description: 'Configurar chaves PIX' },
        { name: 'Notas Fiscais', description: 'Integração com prefeituras' }
      ]
    },
    {
      title: 'Notificações',
      description: 'Configure como receber notificações',
      icon: Bell,
      items: [
        { name: 'Email', description: 'Configurar templates de email' },
        { name: 'SMS', description: 'Configurar envio de SMS' },
        { name: 'Push', description: 'Notificações push no app' }
      ]
    },
    {
      title: 'Segurança',
      description: 'Configurações de segurança e privacidade',
      icon: Shield,
      items: [
        { name: 'Senhas', description: 'Políticas de senha' },
        { name: 'Backup', description: 'Configurar backups automáticos' },
        { name: 'Auditoria', description: 'Logs de atividades' }
      ]
    },
    {
      title: 'Sistema',
      description: 'Configurações gerais do sistema',
      icon: Settings,
      items: [
        { name: 'Idioma', description: 'Português, Inglês' },
        { name: 'Fuso Horário', description: 'Configurar timezone' },
        { name: 'Manutenção', description: 'Modo de manutenção' }
      ]
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Configurações</h1>
          <p className="text-gray-600 dark:text-gray-400">Configure seu sistema e preferências</p>
        </div>
      </div>

      {/* Settings Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {settingsCategories.map((category, index) => (
          <div key={index} className="card p-6 hover:shadow-medium transition-shadow dark:bg-gray-800 dark:border-gray-700">
            <div className="flex items-start gap-x-4">
              <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                <category.icon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  {category.title}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  {category.description}
                </p>
                <div className="space-y-2">
                  {category.items.map((item, itemIndex) => {
                    const handleClick = () => {
                      if ((item as any).href) {
                        navigate((item as any).href);
                      } else if (item.name === 'Usuários Administrativos') {
                        navigate('/configuracoes/usuarios');
                      } else if (item.name === 'Permissões' || item.name === 'Perfis de Acesso') {
                        navigate('/configuracoes/perfis-acesso');
                      } else if (item.name === 'Dados da Empresa') {
                        navigate('/configuracoes/dados-empresa');
                      } else if (item.name === 'Logo e Identidade') {
                        navigate('/configuracoes/logo-identidade');
                      } else if (item.name === 'Contatos') {
                        navigate('/configuracoes/contatos');
                      }
                    };

                    return (
                      <div 
                        key={itemIndex} 
                        className="flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded-md transition-colors"
                        onClick={handleClick}
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{item.description}</p>
                        </div>
                        <button className="text-primary-600 hover:text-primary-700">
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Settings */}
      <div className="card p-6 dark:bg-gray-800 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Configurações Rápidas
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">Notificações por Email</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Receber notificações importantes por email</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" defaultChecked />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">Backup Automático</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Fazer backup dos dados diariamente</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" defaultChecked />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">Modo Escuro</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Usar tema escuro na interface</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  className="sr-only peer" 
                  checked={darkMode}
                  onChange={toggleDarkMode}
                />
                <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Idioma
              </label>
              <select className="input">
                <option>Português (Brasil)</option>
                <option>English</option>
                <option>Español</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fuso Horário
              </label>
              <select className="input">
                <option>America/Sao_Paulo (UTC-3)</option>
                <option>America/New_York (UTC-5)</option>
                <option>Europe/London (UTC+0)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Formato de Data
              </label>
              <select className="input">
                <option>DD/MM/AAAA</option>
                <option>MM/DD/AAAA</option>
                <option>AAAA-MM-DD</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* System Info */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Informações do Sistema
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="p-3 bg-primary-100 rounded-lg w-fit mx-auto mb-3">
              <Database className="h-6 w-6 text-primary-600" />
            </div>
            <h4 className="font-medium text-gray-900">Versão do Sistema</h4>
            <p className="text-sm text-gray-500">v1.0.3</p>
          </div>
          
          <div className="text-center">
            <div className="p-3 bg-success-100 rounded-lg w-fit mx-auto mb-3">
              <Globe className="h-6 w-6 text-success-600" />
            </div>
            <h4 className="font-medium text-gray-900">Última Atualização</h4>
            <p className="text-sm text-gray-500">15/01/2024</p>
          </div>
          
          <div className="text-center">
            <div className="p-3 bg-warning-100 rounded-lg w-fit mx-auto mb-3">
              <Shield className="h-6 w-6 text-warning-600" />
            </div>
            <h4 className="font-medium text-gray-900">Status do Sistema</h4>
            <p className="text-sm text-green-600">Online</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Configuracoes;
